<script lang="ts" setup>
import type { SlideoverMenuId } from '@forgd/ui-kit/blocks/nav'
import { closeSlideover } from '@forgd/ui-kit/blocks/nav'
import { socialLinks } from '#core/utils/external-links'
import { Badge } from '@forgd/ui-kit/badge'

const props = defineProps<{ modelValue?: SlideoverMenuId }>()
const NuxtLink = resolveComponent('NuxtLink')

const drawers = useHomeNavDrawers()
const drawer = computed(() => {
  const value = drawers.value
  if (!props.modelValue || !(props.modelValue in value)) {
    return undefined
  }
  return value[props.modelValue as keyof typeof value]
})

function tag(item: { to?: string, external?: boolean }) {
  if (!item.to) {
    return 'div'
  }
  return item.external ? 'a' : NuxtLink
}
function linkProps(item: { to?: string, external?: boolean }) {
  if (!item.to) {
    return {}
  }
  return item.external ? { href: item.to, target: '_blank', rel: 'noopener noreferrer' } : { to: item.to }
}
</script>

<template>
  <div v-if="drawer" class="flex min-h-screen flex-col text-primal">
    <div class="sticky top-0 z-10 flex items-center justify-between gap-2 border-b border-b-neutral-600 bg-white p-4">
      <div class="flex items-center gap-2">
        <component :is="drawer.icon" class="size-5 shrink-0" aria-hidden="true" />
        <span class="text-xs leading-none font-semibold">{{ drawer.title }}</span>
      </div>
      <Badge v-if="drawer.badge" variant="primary" size="sm">
        {{ drawer.badge }}
      </Badge>
    </div>

    <div class="flex flex-col gap-1 px-4 py-5">
      <div v-if="drawer.tilesHeading" class="pb-2 text-xs font-normal text-placeholder">
        {{ drawer.tilesHeading }}
      </div>
      <component
        :is="tag(tile)"
        v-for="tile in drawer.tiles"
        :key="tile.label"
        v-bind="linkProps(tile)"
        class="flex items-start gap-4 rounded-lg p-2 transition-colors"
        :class="tile.to ? 'hover:bg-neutral-600/30' : 'cursor-default'"
        @click="tile.to && closeSlideover()"
      >
        <img :src="tile.icon" :alt="tile.label" class="size-10 shrink-0">
        <div class="flex flex-col gap-1">
          <span class="text-xs leading-none font-semibold text-primal">{{ tile.label }}</span>
          <p class="text-2xs font-normal text-secondary">
            {{ tile.description }}
          </p>
          <Badge v-if="tile.badge" variant="gray" class="w-fit" size="sm">
            {{ tile.badge }}
          </Badge>
        </div>
      </component>
    </div>

    <div v-if="drawer.links" class="flex flex-col border-t border-t-neutral-600 p-4">
      <component
        :is="tag(link)"
        v-for="link in drawer.links"
        :key="link.label"
        v-bind="linkProps(link)"
        class="rounded-lg px-2 py-3 text-xs leading-none font-semibold text-primal transition-colors hover:bg-neutral-600/30"
        @click="closeSlideover()"
      >
        {{ link.label }}
      </component>
    </div>

    <div v-if="drawer.socials" class="sticky bottom-0 mt-auto flex items-center gap-8 border-t border-t-neutral-600 bg-white px-4 py-5">
      <a
        v-for="social in socialLinks"
        :key="social.label"
        :href="social.href"
        target="_blank"
        rel="noopener noreferrer"
        :aria-label="social.label"
        class="flex size-6 items-center justify-center rounded-md transition-colors hover:bg-neutral-400"
      >
        <img :src="social.icon" :alt="social.label" class="size-4">
      </a>
    </div>
  </div>
</template>
